import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Search, Eye, User, Award, Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const rangos = [
  'Profesional Inmobiliario',
  'Profesional Inmobiliario Avanzado',
  'Licenciado Inmobiliario',
  'Máster Inmobiliario',
  'Máster Inmobiliario Avanzado',
  'Doctor Inmobiliario',
  'Doctor Honoris Causa',
];

function getStatusClass(status) {
  if (status === 'Activo') return 'bg-green-500/10 text-green-400 border-green-500/20';
  if (status === 'Pendiente') return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
  if (status === 'En Revisión') return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
  if (status === 'Suspendido') return 'bg-orange-500/10 text-orange-400 border-orange-500/20';
  if (status === 'Baja') return 'bg-red-500/10 text-red-400 border-red-500/20';
  return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
}

export function AdminProfessionals() {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [professionals, setProfessionals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({ status: '', rango: '', observaciones: '' });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchProfessionals();
  }, [statusFilter]);

  async function fetchProfessionals() {
    setIsLoading(true);
    try {
      let url = `${API}/admin/professionals`;
      if (statusFilter && statusFilter !== 'all') {
        url += `?status=${encodeURIComponent(statusFilter)}`;
      }
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setProfessionals(await response.json());
      }
    } catch (error) {
      console.error('Error fetching professionals:', error);
    } finally {
      setIsLoading(false);
    }
  }

  function handleView(professional) {
    setSelectedProfessional(professional);
    setIsViewModalOpen(true);
  }

  function handleEdit(professional) {
    setSelectedProfessional(professional);
    setEditForm({
      status: professional.estatus,
      rango: professional.rango || '',
      observaciones: professional.observaciones_admin || ''
    });
    setIsEditModalOpen(true);
  }

  async function handleSaveStatus() {
    if (!selectedProfessional) return;
    setIsSaving(true);

    try {
      const response = await fetch(`${API}/admin/professional/${selectedProfessional.id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: editForm.status,
          rango: editForm.rango,
          observaciones: editForm.observaciones
        })
      });

      if (response.ok) {
        setIsEditModalOpen(false);
        fetchProfessionals();
      } else {
        const error = await response.json();
        alert(error.detail || 'Error al actualizar');
      }
    } catch (error) {
      console.error('Error updating professional:', error);
    } finally {
      setIsSaving(false);
    }
  }

  const filteredProfessionals = professionals.filter(function(p) {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    if (p.nombre && p.nombre.toLowerCase().includes(searchLower)) return true;
    if (p.email && p.email.toLowerCase().includes(searchLower)) return true;
    if (p.numero_registro && p.numero_registro.toLowerCase().includes(searchLower)) return true;
    return false;
  });

  function renderProfessionalRow(prof) {
    const statusClass = getStatusClass(prof.estatus);
    return (
      <tr key={prof.id} className="border-b border-[#2a2a2a] hover:bg-[#1a1a1a]">
        <td className="py-4 px-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#2a2a2a] flex items-center justify-center overflow-hidden">
              {prof.foto ? (
                <img src={prof.foto} alt="" className="w-full h-full object-cover" />
              ) : (
                <User className="w-5 h-5 text-gray-500" />
              )}
            </div>
            <div>
              <p className="text-white font-medium">{prof.nombre}</p>
              <p className="text-sm text-gray-500">{prof.email}</p>
            </div>
          </div>
        </td>
        <td className="py-4 px-4">
          <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${statusClass}`}>
            {prof.estatus}
          </span>
        </td>
        <td className="py-4 px-4">
          <span className="text-gray-400 text-sm">{prof.rango || '-'}</span>
        </td>
        <td className="py-4 px-4">
          <span className="text-gray-400 text-sm font-mono">{prof.numero_registro || '-'}</span>
        </td>
        <td className="py-4 px-4">
          <span className="text-gray-400 text-sm">{prof.estado || '-'}</span>
        </td>
        <td className="py-4 px-4">
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => handleView(prof)} className="text-gray-400 hover:text-white hover:bg-[#2a2a2a]">
              <Eye className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => handleEdit(prof)} className="text-gray-400 hover:text-white hover:bg-[#2a2a2a]">
              <Award className="w-4 h-4" />
            </Button>
          </div>
        </td>
      </tr>
    );
  }

  const rows = [];
  for (let i = 0; i < filteredProfessionals.length; i++) {
    rows.push(renderProfessionalRow(filteredProfessionals[i]));
  }

  return (
    <div className="p-8" data-testid="admin-professionals">
      <div className="mb-8">
        <h1 className="font-manrope font-bold text-2xl text-white mb-2">Gestión de Profesionales</h1>
        <p className="text-gray-400">Aprueba, suspende y asigna rangos a los profesionales</p>
      </div>

      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input placeholder="Buscar por nombre, email o registro..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-[#111111] border-[#2a2a2a] text-white" />
        </div>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v)}>
          <SelectTrigger className="w-full md:w-48 bg-[#111111] border-[#2a2a2a] text-white">
            <SelectValue placeholder="Filtrar por estatus" />
          </SelectTrigger>
          <SelectContent className="bg-[#1a1a1a] border-[#2a2a2a]">
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="Pendiente">Pendiente</SelectItem>
            <SelectItem value="En Revisión">En Revisión</SelectItem>
            <SelectItem value="Activo">Activo</SelectItem>
            <SelectItem value="Suspendido">Suspendido</SelectItem>
            <SelectItem value="Baja">Baja</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-[#111111] border border-[#2a2a2a] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-white" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#2a2a2a] bg-[#0a0a0a]">
                  <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Profesional</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Estatus</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Rango</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Registro</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Estado</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Acciones</th>
                </tr>
              </thead>
              <tbody>{rows.length > 0 ? rows : <tr><td colSpan={6} className="py-12 text-center text-gray-500">No se encontraron profesionales</td></tr>}</tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="bg-[#111111] border-[#2a2a2a] text-white max-w-2xl">
          <DialogHeader><DialogTitle className="text-white">Detalles del Profesional</DialogTitle></DialogHeader>
          {selectedProfessional && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-[#2a2a2a] flex items-center justify-center overflow-hidden">
                  {selectedProfessional.foto ? <img src={selectedProfessional.foto} alt="" className="w-full h-full object-cover" /> : <User className="w-8 h-8 text-gray-500" />}
                </div>
                <div>
                  <h3 className="text-xl font-semibold">{selectedProfessional.nombre}</h3>
                  <p className="text-gray-400">{selectedProfessional.email}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-gray-400">Estatus</Label><p className="text-white">{selectedProfessional.estatus}</p></div>
                <div><Label className="text-gray-400">Rango</Label><p className="text-white">{selectedProfessional.rango || '-'}</p></div>
                <div><Label className="text-gray-400">Registro</Label><p className="text-white font-mono">{selectedProfessional.numero_registro || '-'}</p></div>
                <div><Label className="text-gray-400">Ubicación</Label><p className="text-white">{selectedProfessional.ciudad}, {selectedProfessional.estado}</p></div>
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setIsViewModalOpen(false)} className="border-[#2a2a2a] text-gray-400">Cerrar</Button>
                <Button onClick={() => { setIsViewModalOpen(false); handleEdit(selectedProfessional); }} className="bg-white text-black hover:bg-gray-200">Editar Estatus</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="bg-[#111111] border-[#2a2a2a] text-white">
          <DialogHeader><DialogTitle className="text-white">Actualizar Profesional</DialogTitle></DialogHeader>
          {selectedProfessional && (
            <div className="space-y-6">
              <p className="text-gray-400">Actualizando a: <span className="text-white font-medium">{selectedProfessional.nombre}</span></p>
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-300">Estatus</Label>
                  <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                    <SelectTrigger className="mt-1 bg-[#0a0a0a] border-[#2a2a2a] text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-[#2a2a2a]">
                      <SelectItem value="Pendiente">Pendiente</SelectItem>
                      <SelectItem value="En Revisión">En Revisión</SelectItem>
                      <SelectItem value="Activo">Activo</SelectItem>
                      <SelectItem value="Suspendido">Suspendido</SelectItem>
                      <SelectItem value="Baja">Baja</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Rango</Label>
                  <Select value={editForm.rango} onValueChange={(v) => setEditForm({ ...editForm, rango: v })}>
                    <SelectTrigger className="mt-1 bg-[#0a0a0a] border-[#2a2a2a] text-white"><SelectValue placeholder="Seleccionar rango" /></SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-[#2a2a2a]">
                      <SelectItem value="Profesional Inmobiliario">Profesional Inmobiliario</SelectItem>
                      <SelectItem value="Profesional Inmobiliario Avanzado">Profesional Inmobiliario Avanzado</SelectItem>
                      <SelectItem value="Licenciado Inmobiliario">Licenciado Inmobiliario</SelectItem>
                      <SelectItem value="Máster Inmobiliario">Máster Inmobiliario</SelectItem>
                      <SelectItem value="Máster Inmobiliario Avanzado">Máster Inmobiliario Avanzado</SelectItem>
                      <SelectItem value="Doctor Inmobiliario">Doctor Inmobiliario</SelectItem>
                      <SelectItem value="Doctor Honoris Causa">Doctor Honoris Causa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Observaciones</Label>
                  <Textarea value={editForm.observaciones} onChange={(e) => setEditForm({ ...editForm, observaciones: e.target.value })} className="mt-1 bg-[#0a0a0a] border-[#2a2a2a] text-white" placeholder="Notas internas..." rows={3} />
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setIsEditModalOpen(false)} className="border-[#2a2a2a] text-gray-400">Cancelar</Button>
                <Button onClick={handleSaveStatus} disabled={isSaving} className="bg-white text-black hover:bg-gray-200">
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Guardar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
